import { useEffect, useState } from 'react';

import { MtxGrid } from '@meilleurtaux/mtp-uikit-react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router-dom';

import Breadcrumb from '@/components/Breadcrumb/Breadcrumb';
import { Loader } from '@/components/Loader/loader';
import { ROUTE } from '@/constants/route';
import { useAuth } from '@/hooks/useAuth';
import { TRACKING_INSTANCE } from '@/hooks/useTracking/useTrackingInit/useTrackingInit';
import { BreadcrumbItem } from '@/services/types/Breadcrumb';

import styles from './styles.module.css';

export const FaireVersementSignature: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { state } = useLocation();

  const [showLoader, setShowLoader] = useState(true);

  const previousBreadcrumb: BreadcrumbItem = state?.breadcrumbItems?.[state.breadcrumbItems.length - 1] ?? {
    label: 'Mes opérations',
    path: ROUTE.OPERATIONS,
  };

  const breadcrumbItems: BreadcrumbItem[] = [
    previousBreadcrumb,
    { label: 'Signature électronique', path: ROUTE.FAIREVERSEMENTSIGNATURE },
  ];

  const displayIban = (iban: string) => {
    iban = iban.replaceAll(' ', '');

    for (let i = 4; i < iban.length; i += 5) iban = iban.slice(0, i) + ' ' + iban.slice(i);
    return iban;
  };

  useEffect(() => {
    if (user?.id && user.type && user.success) {
      TRACKING_INSTANCE.setTcVars({
        page_nom: 'signature',
        page_template: 'espace client',
        user_id: user.id,
        user_client: user.type === 'Client',
        user_connexion: user.success,
        user_statut_contrat: sessionStorage.getItem('user_statut_contrat'),
        tunnel_produit: JSON.parse(sessionStorage.getItem('parcoursVersementData') || '').contratNomProduit,
        tunnel_metier: 'espace client placement',
        tunnel_sous_metier: JSON.parse(sessionStorage.getItem('parcoursVersementData') || '').contratFamille,
        tunnel_categorie_produit: 'parcours de versement',
      });
      void TRACKING_INSTANCE.triggerEvent('page', undefined, {
        event_id: 'page',
        event_categorie: 'affichage espace client',
        event_nom: 'signature',
        event_page: 'signature',
      });
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [user?.id, user?.type, user?.success]);

  if (!state?.data) {
    return <Navigate to={ROUTE.DASHBOARD} replace />;
  }

  return (
    <MtxGrid className={styles.container}>
      {showLoader && <Loader />}

      <MtxGrid row className={styles.header}>
        <MtxGrid col={12} colXs={12} colSm={12} colMd={12} colLg={12} colXl={12}>
          <Breadcrumb items={breadcrumbItems} goBack={-2} />

          <div>
            <h1>Signature électronique</h1>
            <p>Il ne reste plus qu&apos;à signer pour valider votre versement.</p>
          </div>
        </MtxGrid>
      </MtxGrid>

      <MtxGrid row className={styles.recap}>
        <MtxGrid
          col={12}
          offset={0}
          colXs={12}
          offsetXs={0}
          colSm={10}
          offsetSm={1}
          colMd={10}
          offsetMd={1}
          colLg={6}
          offsetLg={3}
          colXl={6}
          offsetXl={3}
        >
          <span className={styles.tableTitle}>Récapitulatif de votre demande :</span>

          <table className={styles.contractDetailsTable}>
            <tbody className={styles.contractDetailsList}>
              <tr className={styles.contractDetailLine}>
                <td className={styles.contractDetailsTitle}>
                  <p>NOM DU SUPPORT</p>
                </td>
                <td className={styles.contractDetailsValue}>
                  <p>{state.data.nomSupport}</p>
                </td>
              </tr>
              <tr className={styles.contractDetailLine}>
                <td className={styles.contractDetailsTitle}>
                  <p>MONTANT</p>
                </td>
                <td className={styles.contractDetailsValue}>
                  <p>{t('format.currencyFlexible', { value: state.data.montantVersement })}</p>
                </td>
              </tr>

              <tr className={styles.contractDetailLine}>
                <td className={styles.contractDetailsTitle}>
                  <p>TYPE DE VERSEMENT</p>
                </td>
                <td className={styles.contractDetailsValue}>
                  <p>{state.data.typeVersement}</p>
                </td>
              </tr>

              <tr className={styles.contractDetailLine}>
                <td className={styles.contractDetailsTitle}>
                  <p>IBAN</p>
                </td>
                <td className={styles.contractDetailsValue}>
                  <p className={styles.ibanInfo}>{displayIban(state.data.ibanSelected)}</p>
                </td>
              </tr>
            </tbody>
          </table>
        </MtxGrid>
      </MtxGrid>

      <MtxGrid row>
        <MtxGrid
          col={12}
          offset={0}
          colXs={12}
          offsetXs={0}
          colSm={10}
          offsetSm={1}
          colMd={10}
          offsetMd={1}
          colLg={8}
          offsetLg={2}
          colXl={8}
          offsetXl={2}
        >
          <iframe
            className={styles.signatureIframe}
            src={state.data.urlSign}
            width="100%"
            title="Signature électronique du versement"
            onLoad={() => setShowLoader(false)}
          ></iframe>
        </MtxGrid>
      </MtxGrid>
    </MtxGrid>
  );
};
